import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { createChannelModalitySvgElement } from './channelModalityIcons';
import { MODALITIES, scopeIncludes } from './scopeMarkdown';

export const blockScopeKey = new PluginKey('blockScope');

/**
 * Node types that may carry a `scope` attribute (round-tripped to markdown).
 * Includes container types so lists, blockquotes, code blocks and tables can
 * also be scoped via markdown sentinels even though their chip UI is rendered
 * on the leaf paragraph/heading nodes.
 */
const ATTR_NODE_TYPES = [
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'codeBlock',
  'table',
];

/**
 * Node types that render the interactive chip widget. The widget is an inline
 * `float: right` <span>, so we render it inside an inline-content holder (a
 * paragraph or heading). For `listItem`, we render the chip inside its first
 * paragraph child so the row sits on the same visual line as the item.
 */
const CHIP_NODE_TYPES = ['paragraph', 'heading'];
const LIST_CONTAINER_TYPES = ['bulletList', 'orderedList'];

const normaliseScopeAttr = value => {
  if (!value) return null;
  const list = Array.isArray(value) ? value : String(value).split(',');
  const filtered = list
    .map(s => String(s).trim().toLowerCase())
    .filter(s => MODALITIES.includes(s));
  if (filtered.length === 0 || filtered.length === MODALITIES.length) return null;
  return Array.from(new Set(filtered)).sort();
};

const buildChipRow = ({ scope, onToggle, getLabel }) => {
  const row = document.createElement('span');
  row.className = 'tiptap-scope-chips';
  row.contentEditable = 'false';
  MODALITIES.forEach(modality => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.modality = modality;
    const active = scopeIncludes(scope, modality);
    btn.className = `tiptap-scope-icon${active ? ' is-active' : ''}`;
    btn.title = getLabel(modality);
    btn.setAttribute('aria-label', getLabel(modality));
    btn.appendChild(createChannelModalitySvgElement(modality));
    btn.addEventListener('mousedown', event => {
      event.preventDefault();
      event.stopPropagation();
      onToggle(modality);
    });
    row.appendChild(btn);
  });
  return row;
};

const pushChipWidget = (decorations, widgetPos, scopePos, scope, options) => {
  decorations.push(
    Decoration.widget(
      widgetPos,
      () =>
        buildChipRow({
          scope,
          getLabel: options.getLabel,
          onToggle: modality => options.onToggle?.(scopePos, modality),
        }),
      {
        side: -1,
        ignoreSelection: true,
        key: `scope-chips-${scopePos}`,
      },
    ),
  );
};

const buildDecorations = (state, options) => {
  const enabled = !!options.enabled?.();
  if (!enabled) return DecorationSet.empty;
  const decorations = [];

  const visit = (node, basePos, suppressChips) => {
    node.forEach((child, offset) => {
      const pos = basePos + offset;
      const { name } = child.type;

      let scope = null;
      if (ATTR_NODE_TYPES.includes(name)) {
        scope = normaliseScopeAttr(child.attrs.scope);
        if (scope) {
          decorations.push(
            Decoration.node(pos, pos + child.nodeSize, {
              class: 'tiptap-scoped-block',
            }),
          );
        }
      }

      if (!suppressChips && CHIP_NODE_TYPES.includes(name)) {
        pushChipWidget(decorations, pos + 1, pos, scope, options);
      } else if (name === 'listItem') {
        // The widget needs an inline-content host, so target the listItem's
        // first paragraph child. Scope still belongs to the listItem itself.
        const { firstChild } = child;
        if (firstChild && firstChild.type.name === 'paragraph') {
          pushChipWidget(decorations, pos + 2, pos, scope, options);
        }
      }

      if (LIST_CONTAINER_TYPES.includes(name)) {
        // Visit listItems but don't allow them to render their own chip again
        // via their paragraph children (we already rendered one above).
        visit(child, pos + 1, false);
      } else if (name === 'listItem') {
        // Suppress chips on the immediate paragraph children (their chip is
        // the listItem's chip, already placed inside the first paragraph).
        visit(child, pos + 1, true);
      }
    });
  };

  visit(state.doc, 0, false);
  return DecorationSet.create(state.doc, decorations);
};

const BlockScope = Extension.create({
  name: 'blockScope',

  addOptions() {
    return {
      enabled: () => false,
      onToggle: () => {},
      getLabel: modality => modality,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: ATTR_NODE_TYPES,
        attributes: {
          scope: {
            default: null,
            keepOnSplit: true,
            parseHTML: el => {
              const raw = el.getAttribute('data-scope');
              return raw ? normaliseScopeAttr(raw.split(',')) : null;
            },
            renderHTML: attrs => {
              const scope = normaliseScopeAttr(attrs.scope);
              if (!scope) return {};
              return { 'data-scope': scope.join(','), class: 'tiptap-scoped-block' };
            },
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    const { options } = this;
    return [
      new Plugin({
        key: blockScopeKey,
        state: {
          init(_, state) {
            return buildDecorations(state, options);
          },
          apply(tr, value, _oldState, newState) {
            const meta = tr.getMeta(blockScopeKey);
            if (meta || tr.docChanged) {
              return buildDecorations(newState, options);
            }
            return value.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

export default BlockScope;
export { normaliseScopeAttr };
